import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { FiClipboard, FiPenTool, FiCode, FiCheckCircle, FiUploadCloud, FiLifeBuoy, FiMousePointer } from 'react-icons/fi';
import { SiClaude, SiOpenai, SiGooglegemini, SiOllama } from 'react-icons/si';
import { TbPlanet } from 'react-icons/tb';

const PHASE_DURATION = 6000;

const AIToolsSection = styled.section`
  background-color: ${props => props.theme.colors.background};
  position: relative;
  overflow: hidden;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const SectionTitle = styled.h2`
  font-size: clamp(2rem, 4vw, 3rem);
  text-align: center;
  margin-bottom: 1rem;

  .highlight {
    color: ${props => props.theme.colors.primary};
  }
`;

const SectionSubtitle = styled.p`
  color: ${props => props.theme.colors.gray};
  text-align: center;
  max-width: 680px;
  margin: 0 auto 4rem;
  font-size: 1.1rem;
  line-height: 1.7;
`;

const Explorer = styled.div`
  display: grid;
  grid-template-columns: 380px 1fr;
  gap: 2rem;
  align-items: stretch;

  @media (max-width: ${props => props.theme.breakpoints.lg}) {
    grid-template-columns: 1fr;
  }
`;

const StepRail = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;

  @media (max-width: ${props => props.theme.breakpoints.lg}) {
    flex-direction: row;
    overflow-x: auto;
    padding-bottom: 0.5rem;
    scrollbar-width: none;

    &::-webkit-scrollbar {
      display: none;
    }
  }
`;

const StepButton = styled.button`
  position: relative;
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem 1.25rem;
  border-radius: 12px;
  border: 1px solid ${props => (props.$active ? `${props.theme.colors.primary}50` : 'transparent')};
  background: ${props => (props.$active ? props.theme.colors.dark : 'transparent')};
  text-align: left;
  overflow: hidden;
  transition: background 0.3s ease, border-color 0.3s ease;

  &:hover {
    background: ${props => props.theme.colors.dark};
  }

  .step-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    border-radius: 10px;
    flex-shrink: 0;
    font-size: 1.2rem;
    background: ${props => (props.$active ? `${props.theme.colors.primary}20` : `${props.theme.colors.text}08`)};
    color: ${props => (props.$active ? props.theme.colors.primary : props.theme.colors.gray)};
    box-shadow: ${props => (props.$active ? `0 0 18px ${props.theme.colors.primary}30` : 'none')};
    transition: all 0.3s ease;
  }

  .step-number {
    font-family: ${props => props.theme.fonts.heading};
    font-size: 0.72rem;
    font-weight: 600;
    letter-spacing: 0.12em;
    color: ${props => (props.$active ? props.theme.colors.primary : `${props.theme.colors.gray}99`)};
    transition: color 0.3s ease;
  }

  .step-title {
    font-family: ${props => props.theme.fonts.heading};
    font-size: 1rem;
    font-weight: 600;
    color: ${props => (props.$active ? props.theme.colors.text : props.theme.colors.gray)};
    transition: color 0.3s ease;
    white-space: nowrap;
  }

  @media (max-width: ${props => props.theme.breakpoints.lg}) {
    flex-shrink: 0;
  }
`;

const StepProgress = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 2px;
  background: ${props => props.theme.colors.primary}15;

  .fill {
    height: 100%;
    background: ${props => props.theme.colors.primary};
    width: 0;
    animation: fillProgress ${PHASE_DURATION}ms linear forwards;
    animation-play-state: ${props => (props.$paused ? 'paused' : 'running')};
  }

  @keyframes fillProgress {
    from { width: 0; }
    to { width: 100%; }
  }
`;

const Stage = styled.div`
  position: relative;
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: 20px;
  background: ${props => props.theme.colors.dark};
  padding: 3rem;
  min-height: 340px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: -40%;
    right: -20%;
    width: 70%;
    height: 120%;
    background: radial-gradient(circle at center, ${props => props.theme.colors.primary}0e 0%, transparent 65%);
    pointer-events: none;
  }

  @media (max-width: ${props => props.theme.breakpoints.md}) {
    padding: 2rem;
    min-height: 380px;
  }
`;

const Watermark = styled(motion.div)`
  position: absolute;
  top: 1rem;
  right: 2rem;
  font-family: ${props => props.theme.fonts.heading};
  font-size: clamp(6rem, 12vw, 10rem);
  font-weight: 700;
  line-height: 1;
  color: transparent;
  -webkit-text-stroke: 1px ${props => props.theme.colors.primary}25;
  pointer-events: none;
  user-select: none;
`;

const StageContent = styled(motion.div)`
  position: relative;
  z-index: 1;

  .stage-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 56px;
    height: 56px;
    border-radius: 14px;
    background: ${props => props.theme.colors.primary}18;
    color: ${props => props.theme.colors.primary};
    font-size: 1.7rem;
    margin-bottom: 1.5rem;
    box-shadow: 0 0 30px ${props => props.theme.colors.primary}25;
  }

  h3 {
    font-size: clamp(1.5rem, 3vw, 2.2rem);
    margin-bottom: 1rem;
  }

  p {
    color: ${props => props.theme.colors.gray};
    font-size: 1.05rem;
    line-height: 1.75;
    max-width: 560px;
    margin-bottom: 1.75rem;
  }
`;

const AgentChips = styled(motion.div)`
  display: flex;
  flex-wrap: wrap;
  gap: 0.6rem;
`;

const AgentChip = styled(motion.span)`
  padding: 0.5rem 1.1rem;
  border-radius: 999px;
  border: 1px solid ${props => props.theme.colors.primary}35;
  background: ${props => props.theme.colors.primary}10;
  color: ${props => props.theme.colors.primary};
  font-size: 0.85rem;
  font-weight: 500;
`;

const Toolbox = styled.div`
  margin-top: 5rem;
  text-align: center;

  .toolbox-title {
    font-size: 1.5rem;
    margin-bottom: 0.75rem;

    .highlight {
      color: ${props => props.theme.colors.primary};
    }
  }

  .toolbox-subtitle {
    color: ${props => props.theme.colors.gray};
    max-width: 560px;
    margin: 0 auto 2.5rem;
    font-size: 1rem;
    line-height: 1.7;
  }
`;

const ToolsGrid = styled(motion.div)`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.25rem;
  max-width: 980px;
  margin: 0 auto;

  @media (max-width: ${props => props.theme.breakpoints.lg}) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: ${props => props.theme.breakpoints.sm}) {
    grid-template-columns: 1fr;
  }
`;

const ToolCard = styled(motion.div)`
  position: relative;
  display: flex;
  align-items: center;
  gap: 1.1rem;
  padding: 1.4rem 1.5rem;
  border-radius: 16px;
  border: 1px solid ${props => props.theme.colors.border};
  background: ${props => props.theme.colors.dark};
  text-align: left;
  overflow: hidden;
  transition: border-color 0.3s ease, transform 0.3s ease, box-shadow 0.3s ease;

  &:hover {
    border-color: ${props => props.$color}60;
    transform: translateY(-4px);
    box-shadow: 0 12px 30px rgba(0, 0, 0, 0.35), 0 0 24px ${props => props.$color}18;

    .tool-icon {
      transform: scale(1.1) rotate(-4deg);
    }
  }

  .tool-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 52px;
    height: 52px;
    border-radius: 13px;
    flex-shrink: 0;
    font-size: 1.7rem;
    background: ${props => props.$color}16;
    color: ${props => props.$color};
    transition: transform 0.3s ease;
  }

  .tool-info {
    min-width: 0;

    .tool-name {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      font-family: ${props => props.theme.fonts.heading};
      font-size: 1.1rem;
      font-weight: 600;
      color: ${props => props.theme.colors.text};
    }

    .tool-vendor {
      color: ${props => props.$color};
      font-size: 0.75rem;
      font-weight: 600;
      letter-spacing: 0.08em;
      text-transform: uppercase;
      margin-bottom: 0.15rem;
    }

    .tool-use {
      color: ${props => props.theme.colors.gray};
      font-size: 0.85rem;
      line-height: 1.5;
    }
  }

  .cert-tag {
    padding: 0.15rem 0.55rem;
    border-radius: 999px;
    border: 1px solid ${props => props.$color}50;
    background: ${props => props.$color}15;
    color: ${props => props.$color};
    font-family: ${props => props.theme.fonts.main};
    font-size: 0.65rem;
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    white-space: nowrap;
  }
`;

const phases = [
  {
    number: '01',
    title: 'Planning & Requirements',
    icon: <FiClipboard />,
    description:
      'AI agents turn raw stakeholder input into structured requirements — drafting user stories, analyzing documents, and keeping the backlog groomed.',
    agents: ['Story drafting', 'Requirements analysis', 'Backlog grooming'],
  },
  {
    number: '02',
    title: 'Design & Architecture',
    icon: <FiPenTool />,
    description:
      'Copilots explore architecture options, challenge design decisions, and generate diagrams and decision records in minutes instead of days.',
    agents: ['Architecture review', 'Diagram generation', 'ADR drafting'],
  },
  {
    number: '03',
    title: 'Development',
    icon: <FiCode />,
    description:
      'Coding agents implement features, refactor legacy code, and document as they go — with engineers reviewing and steering the work.',
    agents: ['Coding agents', 'Refactoring', 'Auto-documentation'],
  },
  {
    number: '04',
    title: 'Testing & QA',
    icon: <FiCheckCircle />,
    description:
      'AI generates and maintains test suites, reviews every pull request, and hunts for regressions and edge cases humans miss.',
    agents: ['Test generation', 'AI code review', 'Edge-case hunting'],
  },
  {
    number: '05',
    title: 'Deployment & DevOps',
    icon: <FiUploadCloud />,
    description:
      'Agents assist across CI/CD — drafting release notes, validating configurations, and summarizing incidents when things go wrong.',
    agents: ['CI/CD assistants', 'Release notes', 'Incident summaries'],
  },
  {
    number: '06',
    title: 'Maintenance & Support',
    icon: <FiLifeBuoy />,
    description:
      'Support agents resolve user questions, triage bugs from logs, and keep documentation alive as the product evolves.',
    agents: ['Support agents', 'Bug triage', 'Living documentation'],
  },
];

const tools = [
  {
    name: 'Claude',
    vendor: 'Anthropic',
    use: 'AI agents & production apps',
    icon: <SiClaude />,
    color: '#D97757',
    tag: 'Certified',
  },
  {
    name: 'ChatGPT',
    vendor: 'OpenAI',
    use: 'Research & rapid prototyping',
    icon: <SiOpenai />,
    color: '#74AA9C',
  },
  {
    name: 'Gemini',
    vendor: 'Google',
    use: 'Multimodal & long-context work',
    icon: <SiGooglegemini />,
    color: '#8E75B2',
  },
  {
    name: 'Cursor',
    vendor: 'AI IDE',
    use: 'AI-first development',
    icon: <FiMousePointer />,
    color: '#E8E8E8',
  },
  {
    name: 'Antigravity',
    vendor: 'Google',
    use: 'Agent-driven IDE workflows',
    icon: <TbPlanet />,
    color: '#4285F4',
  },
  {
    name: 'Ollama',
    vendor: 'Local models',
    use: 'Private on-prem deployments',
    icon: <SiOllama />,
    color: '#00C8FF',
  },
];

const chipsVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1, delayChildren: 0.25 },
  },
};

const chipVariants = {
  hidden: { opacity: 0, y: 12, scale: 0.9 },
  visible: { opacity: 1, y: 0, scale: 1, transition: { duration: 0.4, ease: 'easeOut' } },
};

const AITools = () => {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const { ref, inView } = useInView({
    threshold: 0.2,
    triggerOnce: false,
  });

  // Auto-advance through phases while the section is visible and not hovered
  useEffect(() => {
    if (paused || !inView) return undefined;
    const timer = setTimeout(() => {
      setActive(a => (a + 1) % phases.length);
    }, PHASE_DURATION);
    return () => clearTimeout(timer);
  }, [active, paused, inView]);

  const phase = phases[active];

  return (
    <AIToolsSection id="ai-tools">
      <Container ref={ref}>
        <SectionTitle>
          AI Across the <span className="highlight">SDLC</span>
        </SectionTitle>
        <SectionSubtitle>
          AI adoption works best when it maps to how software is actually built.
          Explore each phase of the software development lifecycle to see where
          AI agents fit in.
        </SectionSubtitle>

        <Explorer
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
        >
          <StepRail>
            {phases.map((p, i) => (
              <StepButton
                key={p.number}
                type="button"
                $active={i === active}
                onClick={() => setActive(i)}
              >
                <span className="step-icon">{p.icon}</span>
                <span>
                  <span className="step-number">PHASE {p.number}</span>
                  <br />
                  <span className="step-title">{p.title}</span>
                </span>
                {i === active && inView && (
                  <StepProgress $paused={paused}>
                    <div className="fill" key={`${active}-${paused}`} />
                  </StepProgress>
                )}
              </StepButton>
            ))}
          </StepRail>

          <Stage>
            <AnimatePresence mode="wait">
              <Watermark
                key={`wm-${active}`}
                initial={{ opacity: 0, x: 40 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -40 }}
                transition={{ duration: 0.45, ease: 'easeOut' }}
              >
                {phase.number}
              </Watermark>
            </AnimatePresence>
            <AnimatePresence mode="wait">
              <StageContent
                key={active}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -24 }}
                transition={{ duration: 0.45, ease: 'easeOut' }}
              >
                <span className="stage-icon">{phase.icon}</span>
                <h3>{phase.title}</h3>
                <p>{phase.description}</p>
                <AgentChips variants={chipsVariants} initial="hidden" animate="visible">
                  {phase.agents.map(agent => (
                    <AgentChip key={agent} variants={chipVariants}>
                      {agent}
                    </AgentChip>
                  ))}
                </AgentChips>
              </StageContent>
            </AnimatePresence>
          </Stage>
        </Explorer>

        <Toolbox>
          <h3 className="toolbox-title">
            My AI <span className="highlight">Toolbox</span>
          </h3>
          <p className="toolbox-subtitle">
            Hands-on daily with the leading AI platforms and agentic tools —
            including private, local model deployments where data can't leave your infrastructure.
          </p>
          <ToolsGrid
            variants={chipsVariants}
            initial="hidden"
            animate={inView ? 'visible' : 'hidden'}
          >
            {tools.map(tool => (
              <ToolCard key={tool.name} variants={chipVariants} $color={tool.color}>
                <span className="tool-icon">{tool.icon}</span>
                <div className="tool-info">
                  <div className="tool-vendor">{tool.vendor}</div>
                  <div className="tool-name">
                    {tool.name}
                    {tool.tag && <span className="cert-tag">{tool.tag}</span>}
                  </div>
                  <div className="tool-use">{tool.use}</div>
                </div>
              </ToolCard>
            ))}
          </ToolsGrid>
        </Toolbox>
      </Container>
    </AIToolsSection>
  );
};

export default AITools;
